import React from 'react';

import locales from './locales';
import defaultLocales from './default-locales';
import symbols from './symbols';

interface ReactCurrencyFormatterProps {
    quantity: number;
    currency: string,
    symbol?: string;
    locale?: string;
    decimal?: string;
    group?: string;
    pattern?: string;
}
interface FormatterOptions {
    currency?: string,
    symbol?: string;
    locale?: string;
    decimal?: string;
    group?: string;
    pattern?: string;
}
const ReactCurrencyFormatter = (props:ReactCurrencyFormatterProps) => {
    const getFormatter = (options : FormatterOptions) => {
        let locale, currency, symbol: any, pattern, decimal, group;

        // Helper Functions
        const isUndefined = (o : object|string|undefined) => typeof o === 'undefined';

        const toFixed = (n:number, precision:number) => (+(Math.round(+(n + 'e' + precision)) + 'e' + -precision)).toFixed(precision);

        // Perform checks on inputs and set up defaults as needed (defaults to en, USD)
        if(isUndefined(options)) { options = {}; }

        currency = isUndefined(options.currency)? 'EUR' : options.currency?.toUpperCase();
        locale = isUndefined(options.locale) ? (locales as any)[(defaultLocales as any)[currency as string] ] : (locales as any)[options.locale as string];

        if (!isUndefined(locale.h)) locale = (locales as any)[locale.h]; // Locale inheritance

        symbol = isUndefined(options.symbol) ? (symbols as any)[currency as string] : options.symbol;

        if (isUndefined(symbol)) symbol = currency; // In case we don't have the symbol, just use the ccy code

        pattern = isUndefined(options.pattern) ? locale.p : options.pattern;
        decimal = isUndefined(options.decimal) ? locale.d : options.decimal;
        group = isUndefined(options.group) ? locale.g : options.group;

        //console.log(locale);

        // encodePattern Function - returns a few simple characteristics of the pattern provided
        const encodePattern = (pattern:string) => {
            let decimalPlaces = 0;
            let frontPadding = '';
            let backPadding = '';
            let groupLengths = [];

            //console.log(pattern);

            let patternStarted = false;
            let decimalsStarted = false;
            let patternEnded = false;

            let currentGroupLength = 0;
            let zeroLength = 0;

            for(var i = 0; i < pattern.length; ++i ) {
                var c = pattern[i];

                if(!patternStarted && ['#','0',',','.'].indexOf(c) > -1) {
                    patternStarted = true;
                }

                if(!patternStarted) { frontPadding += c; }

                switch (c) {
                    case '#':
                        ++currentGroupLength;
                        break;

                    case '0':
                        if(decimalsStarted) {  ++decimalPlaces; }
                        else { ++currentGroupLength; ++zeroLength; }
                        break;

                    case ',':
                        groupLengths.push(currentGroupLength);
                        currentGroupLength = 0;
                        break;

                    case '.':
                        groupLengths.push(currentGroupLength);
                        decimalsStarted = true;
                        break;
                }

                if(patternStarted && !(['#','0',',','.'].indexOf(c) > -1)) {
                    patternEnded = true;

                    if(!decimalsStarted) {
                        groupLengths.push(currentGroupLength);
                    }
                }

                if(patternEnded) { backPadding += c; }
            }
            let decimal,symbol,group;
            const encodedPattern = {
                decimalPlaces,
                frontPadding,
                backPadding,
                groupLengths,
                zeroLength,
                decimal,symbol,group
            };

            return encodedPattern;
        };

        // Zero Padding helper function
        var pad = (n:string, width:number) => {
            n = n + '';
            return n.length >= width ? n : new Array(width - n.length + 1).join('0') + n;
        }

        // Format function
        const format = (n:number, f: {symbol: any, zeroLength: number, decimalPlaces: number, backPadding: string, frontPadding: string, groupLengths: any[], decimal: any, group: any}
        ) => {
            var formattedNumber = toFixed(Math.abs(n), f.decimalPlaces);

            var splitNumber = formattedNumber.split(".");

            var segment = "";
            // i.e. we actually have some sort of grouping in the values
            if(f.groupLengths.length > 1) {
                var cursor = splitNumber[0].length;
                var groupIndex = f.groupLengths.length - 1;

                while(cursor > 0) {
                    if(groupIndex <= 0) { groupIndex = 1; } // Always reset to the first group length if the number is big

                    var currentGroupLength = f.groupLengths[groupIndex];

                    var start = cursor-currentGroupLength;

                    segment = splitNumber[0].substring(start, cursor) + f.group + segment;

                    cursor -= currentGroupLength;

                    --groupIndex;
                }

                segment = segment.substring(0, segment.length-1);
                //console.log(segment);
            }

            if(segment.length < f.zeroLength) { segment = pad(segment, f.zeroLength); }

            var formattedNumber = f.frontPadding + segment + ( isUndefined(splitNumber[1]) ? '' : (f.decimal + splitNumber[1]) ) + f.backPadding;

            return formattedNumber.replace('!', symbol);
        };

        // Use encode function to work out pattern
        var patternArray = pattern.split(";");
        var positiveFormat = encodePattern(patternArray[0]);

        positiveFormat.symbol = symbol;
        positiveFormat.decimal = decimal;
        positiveFormat.group = group;

        var negativeFormat = isUndefined(patternArray[1]) ? encodePattern("-" + patternArray[0]) : encodePattern(patternArray[1]);

        negativeFormat.symbol = symbol;
        negativeFormat.decimal = decimal;
        negativeFormat.group = group;

        var zero = isUndefined(patternArray[2]) ? format(0, positiveFormat) : patternArray[2];

        let zeroFormat;
        if (!isUndefined(patternArray[2])) {
            zeroFormat = patternArray[2];
        }

        return (n:number) => {
            let formattedNumber;
            n = Number(n);
            if(n > 0) { formattedNumber = format(n, positiveFormat); }
            else if(n == 0) { formattedNumber = zero.replace('!', symbol); }
            else { formattedNumber = format(n, negativeFormat);	}
            return formattedNumber;
        };
    };

    const format = (number:number, options:FormatterOptions) => {
        const formatterFunction = getFormatter(options);

        return formatterFunction(number);
    }

    const {
        quantity,
        currency,
        symbol,
        locale,
        decimal,
        group,
        pattern
    } = props;

    return (format(props.quantity, {
        currency,
        symbol,
        locale,
        decimal,
        group,
        pattern
    }));
};


export default ReactCurrencyFormatter;